import * as React from "react";
import TextField from "@mui/material/TextField";
import {
  Card,
  Button,
  CardContent,
  CardHeader,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { useIsAdmin } from "../../../util/AdminUtil";
import { checkSessionStatus } from "../Session Management/index.js";

const AdminLogin = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionVerifying, setSessionVerifying] = useState(true);
  const [sessionMessage, setSessionMessage] = useState(null);
  const isAdmin = useIsAdmin();
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();

  useEffect(() => {
    const wasAdmin = sessionStorage.getItem("isAdmin") === "true";
    const urlReason = searchParams.get("reason");

    checkSessionStatus()
      .then((status) => {
        if (!status.active) {
          sessionStorage.removeItem("isAdmin");
          sessionStorage.removeItem("adminUser");
          localStorage.removeItem("adminUser");
          localStorage.removeItem("authToken");
          window.dispatchEvent(new Event("adminLoginChange"));

          if (
            urlReason === "unauthorized" ||
            (!urlReason &&
              wasAdmin &&
              (status.httpStatus === 401 || status.httpStatus === 403))
          ) {
            setSessionMessage("unauthorized");
          } else if (urlReason === "expired" || (!urlReason && wasAdmin)) {
            setSessionMessage("expired");
          }
        }
      })
      .catch(() => {
        if (urlReason) setSessionMessage(urlReason);
      })
      .finally(() => {
        setSessionVerifying(false);
      });
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const endpoint = "/user/login.json";
      const response = await fetch(process.env.REACT_APP_API_HOST + endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password }),
        credentials: "include",
      });

      const data = await response.json();

      if (response.ok && data.success) {
        sessionStorage.setItem("isAdmin", "true");
        sessionStorage.setItem("adminUser", JSON.stringify(data.data));
        localStorage.setItem("adminUser", JSON.stringify(data.data));
        localStorage.setItem("authToken", "true");

        window.dispatchEvent(new Event("adminLoginChange"));

        navigate("/Admin/Announcements");
      } else {
        setError(
          data.message || "Login failed. Please check your credentials.",
        );
      }
    } catch (err) {
      setError("An error occurred during login. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("isAdmin");
    sessionStorage.removeItem("adminUser");
    sessionStorage.removeItem("isUser");
    localStorage.removeItem("adminUser");
    localStorage.removeItem("authToken");

    window.dispatchEvent(new Event("adminLoginChange"));
    window.dispatchEvent(new Event("userLoginChange"));

    navigate("/");
  };

  if (sessionVerifying) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isAdmin) {
    const adminUser = JSON.parse(sessionStorage.getItem("adminUser") || "{}");

    return (
      <Box
        sx={{ width: "100%", display: "flex", justifyContent: "center", mt: 4 }}
      >
        <Card sx={{ width: "75%", maxWidth: 500, p: 2 }}>
          <CardHeader title="Admin Account" sx={{ textAlign: "center" }} />
          <CardContent sx={{ textAlign: "center" }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Welcome, {adminUser.name || "Admin"}!
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
              You are currently logged in as an admin.
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="contained"
                onClick={() => navigate("/Admin/Announcements")}
                sx={{ minWidth: 120 }}
              >
                Admin Menu
              </Button>
              <Button
                variant="outlined"
                onClick={handleLogout}
                sx={{ minWidth: 120 }}
              >
                Logout
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{ width: "100%", display: "flex", justifyContent: "center", mt: 4 }}
    >
      <Card sx={{ width: "75%", maxWidth: 500, p: 2 }}>
        <CardHeader title="Admin Login" sx={{ textAlign: "center" }} />
        <CardContent>
          <form onSubmit={handleLogin}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {sessionMessage === "expired" && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  Your session has expired. Please log in again.
                </Alert>
              )}
              {sessionMessage === "unauthorized" && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  You are not authorized to access this page. Please log in.
                </Alert>
              )}
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                fullWidth
                label="Username"
                variant="outlined"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                disabled={loading}
                size="small"
              />

              <TextField
                fullWidth
                label="Password"
                type="password"
                variant="outlined"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                size="small"
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{ mt: 2, py: 1.5 }}
              >
                {loading ? "Logging in..." : "Login"}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

const module = {
  routeProps: {
    path: "AdminLogin",
    component: AdminLogin,
  },
  name: "Admin Login",
  label: "Admin Login",
  title: "Administrator Login",
  showOnMenu: true,
};

export default module;
